import type { Address } from "viem"
import { encodeFunctionData, encodePacked } from "viem"
import { readContract } from "@wagmi/core"
import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ApprovalMode, ContractRiskLevel } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenStore } from "@/domains/tokens"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { erc20Abi, routerAbi } from "@/domains/abis"
import { determineApprovalAmount, OrchestratedQuery, unixTimestamp } from "@/domains/library"
import { THICK_V1_BASE_ADDRESSES } from "@/domains/thick-v1/constants"
import { THICK_V2_BASE_ADDRESSES } from "@/domains/thick-v2/constants"

import type { ScaleRouteCandidate } from "../services/scale-route-finder"
import type { ScaleRouterOnChainTransport } from "../transports/scale-router-on-chain-transport"
import type {
  IAggregatorApiTransport,
  IAggregatorOnChainTransport,
  IPriceImpactCalculator,
  IScaleRouteFinder,
  ISwapAggregatorPlugin,
  ISwapAggregatorSettingsStore,
  ISwapCoordinator,
  ISwapInputStore,
} from "../interfaces"
import type {
  AggregatorQuoteRequest,
  AggregatorTransactionRequest,
  NormalizedSwapQuote,
  NormalizedSwapTransaction,
  SwapQuoteRequestData,
} from "../types"
import {
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapErrorEvent,
  SwapQuoteReceivedEvent,
  SwapQuoteRequestClearedEvent,
  SwapQuoteRequestedEvent,
} from "../events"

type ScaleQuoteCandidate = {
  route: ScaleRouteCandidate
  outputAmountRaw: bigint
  elapsedMs: number
}

const thickV1SwapRouterAbi = [
  {
    inputs: [{ internalType: "bytes[]", name: "data", type: "bytes[]" }],
    name: "multicall",
    outputs: [{ internalType: "bytes[]", name: "results", type: "bytes[]" }],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [],
    name: "refundETH",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "amountMinimum", type: "uint256" },
      { internalType: "address", name: "recipient", type: "address" },
    ],
    name: "unwrapWETH9",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [
      {
        components: [
          { internalType: "bytes", name: "path", type: "bytes" },
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "uint256", name: "amountIn", type: "uint256" },
          { internalType: "uint256", name: "amountOutMinimum", type: "uint256" },
        ],
        internalType: "struct ISwapRouter.ExactInputParams",
        name: "params",
        type: "tuple",
      },
    ],
    name: "exactInput",
    outputs: [{ internalType: "uint256", name: "amountOut", type: "uint256" }],
    stateMutability: "payable",
    type: "function",
  },
] as const

const thickV2SwapRouterAbi = [
  {
    inputs: [{ internalType: "bytes[]", name: "data", type: "bytes[]" }],
    name: "multicall",
    outputs: [{ internalType: "bytes[]", name: "results", type: "bytes[]" }],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [],
    name: "refundETH",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [
      { internalType: "uint256", name: "amountMinimum", type: "uint256" },
      { internalType: "address", name: "recipient", type: "address" },
    ],
    name: "unwrapWETH9",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
  {
    inputs: [
      {
        components: [
          { internalType: "bytes", name: "path", type: "bytes" },
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "uint256", name: "amountIn", type: "uint256" },
          { internalType: "uint256", name: "amountOutMinimum", type: "uint256" },
        ],
        internalType: "struct ISwapRouter.ExactInputParams",
        name: "params",
        type: "tuple",
      },
    ],
    name: "exactInput",
    outputs: [{ internalType: "uint256", name: "amountOut", type: "uint256" }],
    stateMutability: "payable",
    type: "function",
  },
] as const

export class ScaleSwapPlugin implements ISwapAggregatorPlugin {
  readonly id = "scale"
  readonly name = "Scale"
  readonly displayName = "Scale"
  readonly description = "Internal Scale pool routing"
  readonly icon = "/logo-icon.svg"
  readonly color = "from-primary-400 to-p100"
  readonly isProductionReady = true

  latestQuote: NormalizedSwapQuote | null = null
  selectedQuote: NormalizedSwapQuote | null = null

  private quoteQuery: OrchestratedQuery<void> | null = null
  private currentQuoteRequest: SwapQuoteRequestData | null = null
  private initialized = false
  private eventBus: IEventBus | null = null
  private walletStore: IWalletConnectionStore | null = null
  private chainId: number | null = null
  private slippageBps: number | null = null

  constructor(
    private readonly routeFinder: IScaleRouteFinder,
    private readonly onChainTransport: ScaleRouterOnChainTransport,
    private readonly priceImpactCalculatorParam: IPriceImpactCalculator,
    private readonly wrappedNativeToken: TokenStore,
    private readonly getConfig: () => any,
  ) {
    makeObservable(this, {
      latestQuote: observable,
      selectedQuote: observable,
      selectCurrentQuote: action,
      updateSelectedQuote: action,
      clearSelection: action,
      clearQuotes: action,
      displayQuote: computed,
      hasBetterQuote: computed,
      isLoadingQuotes: computed,
    })
  }

  get isLoadingQuotes(): boolean {
    return this.quoteQuery?.isRunning ?? false
  }

  initialize(
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
    swapCoordinator: ISwapCoordinator,
    _inputStore: ISwapInputStore,
    _priceImpactCalculator: IPriceImpactCalculator,
    chainId: number,
    _nativeToken: TokenStore,
    walletStore: IWalletConnectionStore,
    slippageBps: number,
    _settingsStore: ISwapAggregatorSettingsStore,
    _apiTransport?: IAggregatorApiTransport,
    _onChainTransport?: IAggregatorOnChainTransport,
  ): void {
    if (this.initialized) {
      throw new Error("Scale plugin already initialized")
    }

    this.eventBus = eventBus
    this.walletStore = walletStore
    this.chainId = chainId
    this.slippageBps = slippageBps

    const queryName = "Scale Quotes"
    this.quoteQuery = new OrchestratedQuery<void>(
      queryName,
      () => this.fetchQuoteInternal(),
      10_000,
      this.onQuoteSuccess.bind(this),
      this.onQuoteFailed.bind(this),
      orchestratedQueryStore,
    )

    swapCoordinator.registerQuoteQuery(queryName)
    eventBus.subscribe(SwapQuoteRequestedEvent.TYPE, this.handleQuoteRequested.bind(this))
    eventBus.subscribe(SwapQuoteRequestClearedEvent.TYPE, this.handleQuoteRequestCleared.bind(this))

    this.initialized = true
  }

  supportsDirectTransaction(): boolean {
    return true
  }

  supportsApiTransport(): boolean {
    return false
  }

  supportsOnChainTransport(): boolean {
    return true
  }

  async fetchQuote(params: AggregatorQuoteRequest): Promise<void> {
    const quote = await this.buildScaleQuote(params)
    runInAction(() => {
      this.latestQuote = quote
      if (!quote) {
        this.selectedQuote = null
      }
    })

    if (quote && this.eventBus) {
      this.eventBus.publish(new SwapQuoteReceivedEvent(quote))
    }
  }

  async buildTransaction(
    quote: NormalizedSwapQuote,
    _params: AggregatorTransactionRequest,
  ): Promise<NormalizedSwapTransaction | null> {
    return quote.transactionData
      ? {
          ...quote.transactionData,
          gasLimit: quote.gasEstimate?.gasLimit ?? 500000n,
        }
      : null
  }

  selectCurrentQuote(): void {
    this.selectedQuote = this.latestQuote
  }

  updateSelectedQuote(quote: NormalizedSwapQuote): void {
    this.selectedQuote = quote
  }

  clearSelection(): void {
    this.selectedQuote = null
  }

  clearQuotes(): void {
    runInAction(() => {
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  get displayQuote(): NormalizedSwapQuote | null {
    return this.selectedQuote ?? this.latestQuote
  }

  get hasBetterQuote(): boolean {
    if (!this.selectedQuote || !this.latestQuote) {
      return false
    }
    return this.latestQuote.outputAmountRaw > this.selectedQuote.outputAmountRaw
  }

  async getAllowance(token: TokenStore, spender: Address, userAddress: Address): Promise<bigint> {
    try {
      const allowance = await readContract(this.getConfig(), {
        address: token.address.toViemAddress(),
        abi: erc20Abi,
        functionName: "allowance",
        args: [userAddress, spender],
        chainId: this.chainId ?? token.chainId,
      })
      return allowance as bigint
    } catch {
      return 0n
    }
  }

  async executeAllowanceTransaction(
    quote: NormalizedSwapQuote,
    currentAllowance: bigint,
    _userAddress: Address,
    approvalMode: ApprovalMode,
    contractRiskLevel: ContractRiskLevel,
  ): Promise<NormalizedSwapTransaction | null> {
    if (currentAllowance >= quote.inputAmountRaw) {
      this.eventBus?.publish(
        new SwapApprovalCompletedEvent(quote, "0x0000000000000000000000000000000000000000000000000000000000000000"),
      )
      return null
    }

    this.eventBus?.publish(new SwapApprovalStartedEvent(quote))

    const targetAddress = quote.transactionMetadata.targetAddress as Address | undefined
    if (!targetAddress) {
      throw new Error("Scale router address missing from quote metadata")
    }

    const approvalAmount = determineApprovalAmount(quote.inputAmountRaw, approvalMode, contractRiskLevel)
    const data = encodeFunctionData({
      abi: erc20Abi,
      functionName: "approve",
      args: [targetAddress, approvalAmount],
    })

    return {
      to: quote.inputToken.address.toViemAddress(),
      data,
      value: 0n,
      gasLimit: 50000n,
    }
  }

  async executeSwapTransaction(quote: NormalizedSwapQuote, userAddress: Address): Promise<NormalizedSwapTransaction | null> {
    return this.buildTransaction(quote, { quote, userAddress })
  }

  private async fetchQuoteInternal(): Promise<void> {
    if (!this.currentQuoteRequest || !this.walletStore) {
      return
    }

    const userAddress = this.walletStore.safeLensAddress

    const { inputToken, outputToken, inputAmount, slippageBps, chainId, nativeToken } = this.currentQuoteRequest
    if (!inputToken || !outputToken || !inputAmount || inputAmount === "0") {
      return
    }

    try {
      const inputAmountRaw = inputToken.parseUnits(inputAmount)
      await this.fetchQuote({
        chainId,
        inputToken,
        outputToken,
        inputAmountRaw,
        inputAmountFormatted: inputAmount,
        slippageBps,
        userAddress,
        nativeToken,
      })
    } catch (error) {
      console.error("[Scale] Error fetching internal quote:", error)
    }
  }

  private async buildScaleQuote(request: AggregatorQuoteRequest): Promise<NormalizedSwapQuote | null> {
    if (!this.canUseInternalRouting(request.inputToken, request.outputToken, request.nativeToken)) {
      return null
    }

    const routeInputToken = this.isNativeToken(request.inputToken, request.nativeToken)
      ? this.wrappedNativeToken
      : request.inputToken
    const routeOutputToken = this.isNativeToken(request.outputToken, request.nativeToken)
      ? this.wrappedNativeToken
      : request.outputToken

    const routes = this.dedupeRoutes([
      ...this.routeFinder.findRoutes(routeInputToken, routeOutputToken),
      ...(await this.onChainTransport.findDirectClassicRoutes(routeInputToken, routeOutputToken)),
    ])
    if (routes.length === 0) {
      return null
    }

    const quotedRoutes = await this.quoteRoutes(request.inputAmountRaw, routes)
    const bestRoute = quotedRoutes.sort((a, b) => {
      if (b.outputAmountRaw > a.outputAmountRaw) return 1
      if (b.outputAmountRaw < a.outputAmountRaw) return -1
      return 0
    })[0]

    if (!bestRoute) {
      return null
    }

    const outputAmountFormatted = request.outputToken.formatUnits(bestRoute.outputAmountRaw)
    const exchangeRate =
      request.inputAmountRaw > 0n
        ? (Number(bestRoute.outputAmountRaw) / Number(request.inputAmountRaw)).toString()
        : "0"
    const inputAmountUsd = Number(request.inputAmountFormatted) * request.inputToken.bestPrice
    const outputAmountUsd = Number(outputAmountFormatted) * request.outputToken.bestPrice
    const priceImpactBps = this.priceImpactCalculatorParam.calculate(inputAmountUsd, outputAmountUsd)
    const transactionData = this.buildRouterTransaction(request, bestRoute.route, bestRoute.outputAmountRaw)
    const targetAddress = this.getRouteSwapTarget(bestRoute.route)

    return {
      aggregatorId: this.id,
      quoteId: `scale-${bestRoute.route.id}`,
      inputToken: request.inputToken,
      outputToken: request.outputToken,
      inputAmountRaw: request.inputAmountRaw,
      outputAmountRaw: bestRoute.outputAmountRaw,
      inputAmountFormatted: request.inputAmountFormatted,
      outputAmountFormatted,
      exchangeRate,
      priceImpactBps,
      gasEstimate: {
        gasLimit: this.estimateRouteGasLimit(bestRoute.route),
      },
      transactionData,
      transactionMetadata: {
        targetAddress,
        route: bestRoute.route.hops,
        routeSource: bestRoute.route.source,
        routeElapsedMs: bestRoute.elapsedMs,
        source: "scale",
      },
      timestamp: unixTimestamp(),
    }
  }

  private async quoteRoutes(amountIn: bigint, routes: ScaleRouteCandidate[]): Promise<ScaleQuoteCandidate[]> {
    const startedAt = performance.now()
    const routeResults = await Promise.all(
      routes.map(async (route) => {
        try {
          const outputAmountRaw = await this.onChainTransport.quoteRoute(amountIn, route)
          if (!outputAmountRaw || outputAmountRaw <= 0n) {
            return null
          }
          return {
            route,
            outputAmountRaw,
            elapsedMs: Math.round(performance.now() - startedAt),
          }
        } catch {
          return null
        }
      }),
    )

    return routeResults.filter((route): route is ScaleQuoteCandidate => Boolean(route))
  }

  private dedupeRoutes(routes: ScaleRouteCandidate[]): ScaleRouteCandidate[] {
    const routesByKey = new Map<string, ScaleRouteCandidate>()

    for (const route of routes) {
      const key = route.hops.map((hop) => `${hop.from}-${hop.to}-${hop.stable}`).join("|")
      if (!routesByKey.has(key)) {
        routesByKey.set(key, route)
      }
    }

    return Array.from(routesByKey.values())
  }

  private buildRouterTransaction(
    request: AggregatorQuoteRequest,
    route: ScaleRouteCandidate,
    outputAmountRaw: bigint,
  ): NormalizedSwapTransaction {
    if (route.source === "thick-v1" || route.source === "thick-v2") {
      return this.buildConcentratedLiquidityRouterTransaction(request, route, outputAmountRaw)
    }

    const routerRoutes = route.hops.map((hop) => ({
      from: hop.from,
      to: hop.to,
      stable: hop.stable,
    }))
    const amountOutMin = this.applySlippage(outputAmountRaw, request.slippageBps)
    const safeAmountOutMin = amountOutMin > 0n ? amountOutMin : 1n
    const deadline = BigInt(Math.floor(Date.now() / 1000) + 20 * 60)
    const isInputNative = this.isNativeToken(request.inputToken, request.nativeToken)
    const isOutputNative = this.isNativeToken(request.outputToken, request.nativeToken)

    if (isInputNative) {
      return {
        to: this.onChainTransport.getRouterAddress(),
        data: encodeFunctionData({
          abi: routerAbi,
          functionName: "swapExactETHForTokensSupportingFeeOnTransferTokens",
          args: [safeAmountOutMin, routerRoutes, request.userAddress, deadline],
        }),
        value: request.inputAmountRaw,
        gasLimit: this.estimateRouteGasLimit(route),
      }
    }

    if (isOutputNative) {
      return {
        to: this.onChainTransport.getRouterAddress(),
        data: encodeFunctionData({
          abi: routerAbi,
          functionName: "swapExactTokensForETHSupportingFeeOnTransferTokens",
          args: [request.inputAmountRaw, safeAmountOutMin, routerRoutes, request.userAddress, deadline],
        }),
        value: 0n,
        gasLimit: this.estimateRouteGasLimit(route),
      }
    }

    return {
      to: this.onChainTransport.getRouterAddress(),
      data: encodeFunctionData({
        abi: routerAbi,
        functionName: "swapExactTokensForTokensSupportingFeeOnTransferTokens",
        args: [request.inputAmountRaw, safeAmountOutMin, routerRoutes, request.userAddress, deadline],
      }),
      value: 0n,
      gasLimit: this.estimateRouteGasLimit(route),
    }
  }

  private buildConcentratedLiquidityRouterTransaction(
    request: AggregatorQuoteRequest,
    route: ScaleRouteCandidate,
    outputAmountRaw: bigint,
  ): NormalizedSwapTransaction {
    if (route.source !== "thick-v1" && route.source !== "thick-v2") {
      throw new Error("Expected concentrated liquidity route")
    }

    const amountOutMinimum = this.applySlippage(outputAmountRaw, request.slippageBps)
    const safeAmountOutMinimum = amountOutMinimum > 0n ? amountOutMinimum : 1n
    const routerAddress = this.getRouteSwapTarget(route)
    const isInputNative = this.isNativeToken(request.inputToken, request.nativeToken)
    const isOutputNative = this.isNativeToken(request.outputToken, request.nativeToken)
    const recipient = isOutputNative ? routerAddress : request.userAddress

    if (route.source === "thick-v1") {
      const exactInputData = encodeFunctionData({
        abi: thickV1SwapRouterAbi,
        functionName: "exactInput",
        args: [
          {
            path: encodePacked(["address", "uint24", "address"], [route.hops[0]!.from, route.clPool.feeTier, route.hops[0]!.to]),
            recipient,
            amountIn: request.inputAmountRaw,
            amountOutMinimum: safeAmountOutMinimum,
          },
        ],
      })
      const data = isOutputNative
        ? encodeFunctionData({
            abi: thickV1SwapRouterAbi,
            functionName: "multicall",
            args: [
              [
                exactInputData,
                encodeFunctionData({
                  abi: thickV1SwapRouterAbi,
                  functionName: "unwrapWETH9",
                  args: [safeAmountOutMinimum, request.userAddress],
                }),
              ],
            ],
          })
        : exactInputData

      return {
        to: routerAddress,
        data,
        value: isInputNative ? request.inputAmountRaw : 0n,
        gasLimit: this.estimateRouteGasLimit(route),
      }
    }

    if (!route.clPool.tickSpacing) {
      throw new Error("Thick v2 route missing tick spacing")
    }

    const exactInputData = encodeFunctionData({
      abi: thickV2SwapRouterAbi,
      functionName: "exactInput",
      args: [
        {
          path: encodePacked(["address", "int24", "address"], [route.hops[0]!.from, route.clPool.tickSpacing, route.hops[0]!.to]),
          recipient,
          amountIn: request.inputAmountRaw,
          amountOutMinimum: safeAmountOutMinimum,
        },
      ],
    })
    const data = isOutputNative
      ? encodeFunctionData({
          abi: thickV2SwapRouterAbi,
          functionName: "multicall",
          args: [
            [
              exactInputData,
              encodeFunctionData({
                abi: thickV2SwapRouterAbi,
                functionName: "unwrapWETH9",
                args: [safeAmountOutMinimum, request.userAddress],
              }),
            ],
          ],
        })
      : exactInputData

    return {
      to: routerAddress,
      data,
      value: isInputNative ? request.inputAmountRaw : 0n,
      gasLimit: this.estimateRouteGasLimit(route),
    }
  }

  private getRouteSwapTarget(route: ScaleRouteCandidate): Address {
    if (route.source === "thick-v1") {
      return THICK_V1_BASE_ADDRESSES.swapRouter
    }
    if (route.source === "thick-v2") {
      return THICK_V2_BASE_ADDRESSES.swapRouter02
    }
    return this.onChainTransport.getRouterAddress()
  }

  private estimateRouteGasLimit(route: ScaleRouteCandidate): bigint {
    if (route.source === "thick-v1" || route.source === "thick-v2") {
      return 350000n
    }
    return BigInt(260000 + route.hops.length * 90000)
  }

  private applySlippage(amount: bigint, slippageBps: number): bigint {
    return (amount * BigInt(Math.max(0, 10_000 - slippageBps))) / 10_000n
  }

  private canUseInternalRouting(inputToken: TokenStore, outputToken: TokenStore, _nativeToken: TokenStore): boolean {
    return !inputToken.address.equals(outputToken.address)
  }

  private isNativeToken(token: TokenStore, nativeToken: TokenStore): boolean {
    return token.address.equals(nativeToken.address)
  }

  private handleQuoteRequested(event: SwapQuoteRequestedEvent): void {
    runInAction(() => {
      this.currentQuoteRequest = event.quoteRequest
    })

    if (this.quoteQuery) {
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleQuoteRequestCleared(_event: SwapQuoteRequestClearedEvent): void {
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }
    runInAction(() => {
      this.currentQuoteRequest = null
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  private onQuoteSuccess(_sender: IOrchestratedQuerySender<void>, _data: void): void {
    // Quote state is updated by fetchQuoteInternal.
    this.quoteQuery?.stop()
  }

  private onQuoteFailed(_sender: IOrchestratedQuerySender<void>, error: Error): void {
    this.quoteQuery?.stop()
    this.eventBus?.publish(new SwapErrorEvent(this.latestQuote, `Scale quote failed: ${error.message}`))
  }
}
